/**
 * @license
 * @copyright Copyright Motili Inc., 2021 All Rights Reserved
 */

import {
    DropDownSelect,
    Heading1,
    Paragraph,
    RadioGroup,
    SubText,
} from '@motili/ui';
import { ChangeEvent, ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import {
    setCompanyInfo,
    setRegisterForLaborJobs,
} from '../../../common/redux/ducks/contractorRegistrationForm';
import { useAppDispatch, useAppSelector } from '../../../common/redux/hooks';
import {
    Address,
    DropdownOption,
    PartsEquipmentOrdering as PartsEquipmentOrderingForm,
} from '../../../common/types';
import CenteredContainer from '../../../components/layout/CenteredContainer';
import StyledTextInput from '../../../components/styled/StyledTextInput';
import BottomButtonNavigation from '../../../navigation/BottomButtonNavigation';
import AddressForm from './AddressForm';
import UseCompanyAsBillingCheckbox from './UseCompanyAsBillingCheckbox';
import { corporationTypes } from '../../../common/constants';
import PartsEquipmentOrdering from './PartsEquipmentOrdering';

interface YourCompanyProps {
    handleBackClick: () => void;
    handleNextClick: () => void;
}

const companyWebsiteLengthLimit = 100;

export default function YourCompany({
    handleBackClick,
    handleNextClick,
}: YourCompanyProps): ReactElement {
    const dispatch = useAppDispatch();
    const { companyInfo, registerForLaborJobs, partsEquipmentOrdering } =
        useAppSelector(
            ({ contractorRegistration }) => contractorRegistration.contractor
        );

    const {
        register,
        formState: { errors, isValid },
    } = useForm({
        mode: 'all',
        defaultValues: {
            name: companyInfo.name,
            'labor-jobs-checkbox': registerForLaborJobs,
        },
    });

    const showBillingAddressForm = () => (
        <AddressForm addressType='billingAddress' companyInfo={companyInfo} />
    );

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        dispatch(setCompanyInfo({ ...companyInfo, [name]: value }));
    };

    const handleCompanyTypeChange = (data: DropdownOption) => {
        dispatch(
            setCompanyInfo({
                ...companyInfo,
                ['corporationType']: data.value,
            })
        );
    };

    const handleRegisterForLaborJobsChange = (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        dispatch(setRegisterForLaborJobs(event.target.value === 'true'));
    };

    const companyAddressKeys = Object.keys(companyInfo.companyAddress);
    const billingAddressKeys = Object.keys(companyInfo.billingAddress);

    const missingCompanyAddressFields =
        companyAddressKeys
            .filter(key => key !== 'address2')
            .filter(key => companyInfo.companyAddress[key as keyof Address])
            .length < 4;

    const missingBillingAddressFields =
        billingAddressKeys
            .filter(key => key !== 'address2')
            .filter(key => companyInfo.billingAddress[key as keyof Address])
            .length < 4;

    function checkZip() {
        return (
            !/(^\d{5}$)/.test(companyInfo.companyAddress.zip) ||
            !/(^\d{5}$)/.test(companyInfo.billingAddress.zip)
        );
    }

    return (
        <CenteredContainer>
            <Heading1>Your Company</Heading1>
            <Paragraph>
                The Information will be used in a way to group your own network
                of contractors. Even if you don’t have a larger network, we
                still like to have this information on hand to keep our system
                strong.
            </Paragraph>
            <StyledTextInput
                marginTop='20px'
                withLabelSpacing
                required
                name={'name'}
                error={!!errors.name}
                errorMessage={'Name cannot be blank'}
                register={register('name', { required: true })}
                onChange={handleInputChange}
                value={companyInfo.name}
                style={inputStyles}
                label='Company Name'
            />
            <br />
            <DropDownSelect
                required
                value={corporationTypes.find(
                    option => option.value === companyInfo.corporationType
                )}
                name={'corporationType'}
                closeMenuOnSelect={false}
                onChange={option =>
                    handleCompanyTypeChange(option as DropdownOption)
                }
                label='COMPANY TYPE'
                options={corporationTypes}
                placeholder='Select'
            />
            <StyledTextInput
                marginTop='20px'
                withLabelSpacing
                maxLength={companyWebsiteLengthLimit}
                name={'website'}
                onChange={handleInputChange}
                value={companyInfo.website}
                style={inputStyles}
                label={`Company Website`}
            />
            <SubText>({companyWebsiteLengthLimit} max characters)</SubText>
            <AddressForm
                addressType={'companyAddress'}
                companyInfo={companyInfo}
            />
            <UseCompanyAsBillingCheckbox companyInfo={companyInfo} />
            {companyInfo.useCompanyAddressAsBilling
                ? null
                : showBillingAddressForm()}
            <br />
            <RadioGroup
                className=''
                key={'labor-job-selection'}
                onChange={handleRegisterForLaborJobsChange}
                name={'labor-job-selection'}
                options={registerForLaborOptions.map(opt => ({
                    ...opt,
                    checked: String(registerForLaborJobs) === opt.value,
                }))}
                required
                label={'REGISTER MY COMPANY FOR LABOR JOBS'}
            />
            <br />
            <PartsEquipmentOrdering />
            <BottomButtonNavigation
                onBackClick={handleBackClick}
                onNextClick={handleNextClick}
                nextDisabled={
                    !isValid ||
                    !companyInfo.corporationType ||
                    checkZip() ||
                    missingCompanyAddressFields ||
                    missingBillingAddressFields ||
                    !isPartsEquipmentOrderingValid(partsEquipmentOrdering)
                }
            />
        </CenteredContainer>
    );
}

export const isPartsEquipmentOrderingValid = ({
    forMyCompany,
    onBehalfOfClient,
    neither,
    clients,
}: PartsEquipmentOrderingForm): boolean => {
    const hasSelection = forMyCompany || onBehalfOfClient || neither;
    return hasSelection && (!onBehalfOfClient || clients.length > 0);
};

const inputStyles = { width: '100%' };

const registerForLaborOptions = [
    { value: 'true', label: 'Yes' },
    {
        value: 'false',
        label: 'No',
    },
];
